import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getToken, setToken, clearToken, loadRoles } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyLoading, setCompanyLoading] = useState(false);

  const refreshCompany = useCallback(async () => {
    setCompanyLoading(true);
    try {
      const { data } = await api.get("/company");
      setCompany(data);
      return data;
    } catch {
      setCompany(null);
      return null;
    } finally {
      setCompanyLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!getToken()) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then(async ({ data }) => {
        setUser(data);
        await Promise.all([loadRoles().catch(() => {}), refreshCompany()]);
      })
      .catch(() => clearToken())
      .finally(() => setLoading(false));
  }, [refreshCompany]);

  const login = async (email, password, remember = true) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token, remember);
    setUser(data.user);
    await Promise.all([loadRoles().catch(() => {}), refreshCompany()]);
    return data.user;
  };

  const logout = () => {
    clearToken();
    setUser(null);
    setCompany(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, company, loading, companyLoading, login, logout, setUser, refreshCompany }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
